/**
 * useFileUpload.ts
 * 
 * Custom hook for managing file uploads in the application.
 * Provides state management and methods for handling file uploads.
 */

import { useState, useRef, useEffect } from 'react';
import { FileAttachment } from '@/types/posit';
import { useToast } from '@/hooks/use-toast';
import { 
  fileToAttachment, 
  uploadFilesToStorage, 
  storeFileReferences,
  revokeObjectURL,
  revokeAllObjectURLs
} from '@/utils/fileUpload';
import {
  validateFile,
  validateFileAttachment
} from '@/utils/fileValidation';
import { handleError, ErrorCategory } from '@/utils/errorHandling';

interface UseFileUploadOptions {
  positId?: string;
  userId?: string;
}

interface UseFileUploadReturn {
  attachedFiles: FileAttachment[];
  fileMap: Map<string, File>;
  selectedFile: FileAttachment | null;
  isUploading: boolean;
  addFile: (file: File) => FileAttachment | undefined;
  addGoogleDriveFile: (file: FileAttachment) => FileAttachment;
  removeFile: (id: string) => void;
  selectFile: (id: string) => void;
  clearFiles: () => void;
  uploadFiles: () => Promise<FileAttachment[]>;
  isUploadLocked: boolean;
}

export function useFileUpload({ positId, userId }: UseFileUploadOptions = {}): UseFileUploadReturn {
  const [attachedFiles, setAttachedFiles] = useState<FileAttachment[]>([]);
  const [selectedFile, setSelectedFile] = useState<FileAttachment | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  // Add a lock to prevent concurrent uploads
  const [isUploadLocked, setIsUploadLocked] = useState(false);
  const fileMap = useRef<Map<string, File>>(new Map());
  const uploadInProgressRef = useRef<boolean>(false);
  const { toast } = useToast();

  // Cleanup object URLs when component unmounts
  useEffect(() => {
    return () => {
      
      // Revoke all object URLs when the component unmounts
      revokeAllObjectURLs();
      
      // Clear the file map
      fileMap.current.clear();
      
      // Reset upload state
      uploadInProgressRef.current = false;
    };
  }, []);

  // Add a file to the list of attached files
  const addFile = (file: File) => {
    // Prevent adding files during upload
    if (isUploadLocked || uploadInProgressRef.current) {
      toast({
        title: "Upload in progress",
        description: "Please wait for the current upload to complete",
        variant: "destructive"
      });
      return;
    }
    
    // Validate the file using the centralized validation utility
    const validation = validateFile(file);
    
    if (!validation.valid) {
      toast({
        title: validation.error?.includes("size") ? "File too large" : "Unsupported file type",
        description: validation.error,
        variant: "destructive"
      });
      return;
    }

    // Check for duplicate file names
    const isDuplicate = attachedFiles.some(
      existingFile => existingFile.name === file.name && existingFile.source === 'upload'
    );
    
    if (isDuplicate) {
      toast({
        title: "Duplicate file",
        description: `A file named "${file.name}" is already attached`,
        variant: "destructive"
      });
      return;
    }

    // Convert the file to a FileAttachment
    const attachment = fileToAttachment(file);
    
    // Store the file in the map
    fileMap.current.set(attachment.id, file);
    
    // Add the attachment to the list
    setAttachedFiles(prev => [...prev, attachment]);
    
    return attachment;
  };

  // Add a Google Drive file to the list of attached files
  const addGoogleDriveFile = (file: FileAttachment) => {
    // Prevent adding files during upload
    if (isUploadLocked || uploadInProgressRef.current) {
      toast({
        title: "Upload in progress",
        description: "Please wait for the current upload to complete",
        variant: "destructive"
      });
      return file;
    }
    
    // Validate the file using the centralized validation utility
    const validation = validateFileAttachment(file);
    
    if (!validation.valid) {
      toast({
        title: validation.error?.includes("size") ? "File too large" : "Unsupported file type",
        description: validation.error,
        variant: "destructive"
      });
      return file;
    }
    
    // Check for duplicate file IDs
    const isDuplicate = attachedFiles.some(existingFile => existingFile.id === file.id);
    
    if (isDuplicate) {
      toast({
        title: "File already attached",
        description: `"${file.name}" is already attached`,
        variant: "destructive"
      });
      return file;
    }
    
    // Add the attachment to the list
    setAttachedFiles(prev => [...prev, file]);
    
    return file;
  };

  // Remove a file from the list of attached files
  const removeFile = (id: string) => {
    // Prevent removing files during upload
    if (isUploadLocked || uploadInProgressRef.current) {
      toast({
        title: "Upload in progress",
        description: "Cannot remove files during upload",
        variant: "destructive"
      });
      return;
    }
    
    // Find the file to remove
    const fileToRemove = attachedFiles.find(file => file.id === id);
    
    // Revoke the object URL if it's a local upload
    if (fileToRemove && fileToRemove.source === 'upload' && fileToRemove.url) {
      revokeObjectURL(fileToRemove.url);
    }
    
    // Remove the file from the map
    fileMap.current.delete(id);
    
    // Remove the attachment from the list
    setAttachedFiles(prev => prev.filter(file => file.id !== id));
    
    // Clear the selected file if it was removed
    if (selectedFile && selectedFile.id === id) {
      setSelectedFile(null);
    }
  };

  // Select a file for preview
  const selectFile = (id: string) => {
    const file = attachedFiles.find(file => file.id === id);
    setSelectedFile(file || null);
  };

  // Clear all files
  const clearFiles = () => {
    // Prevent clearing files during upload
    if (isUploadLocked || uploadInProgressRef.current) {
      return;
    }
    
    // Revoke all object URLs for uploaded files
    attachedFiles.forEach(file => {
      if (file.source === 'upload' && file.url) {
        revokeObjectURL(file.url);
      }
    });
    
    // Clear the file map
    fileMap.current.clear();
    
    // Clear the attachments
    setAttachedFiles([]);
    
    // Clear the selected file
    setSelectedFile(null);
  };

  // Upload all files to storage
  const uploadFiles = async (): Promise<FileAttachment[]> => {
    if (!userId || !positId) {
      handleError('User ID or Posit ID is missing', 'uploadFiles', {
        category: ErrorCategory.VALIDATION,
        toastTitle: "Upload Error"
      });
      return attachedFiles;
    }

    if (attachedFiles.length === 0) {
      return [];
    }

    // Check if upload is already in progress
    if (uploadInProgressRef.current || isUploadLocked) {
      handleError('Upload already in progress', 'uploadFiles', {
        category: ErrorCategory.VALIDATION,
        toastTitle: "Upload in Progress"
      });
      return attachedFiles;
    }

    // Set locks to prevent concurrent uploads
    uploadInProgressRef.current = true;
    setIsUploading(true);
    setIsUploadLocked(true);

    try {
      
      // Create a copy of the current attachedFiles to prevent race conditions
      const filesToProcess = [...attachedFiles];
      
      // Prepare files for upload
      const filesToUpload = filesToProcess
        .filter(attachment => attachment.source === 'upload')
        .map(attachment => {
          const file = fileMap.current.get(attachment.id);
          if (!file) {
            throw new Error(`File not found for attachment: ${attachment.name}`);
          }
          return { file, attachment };
        });

      // Get Google Drive files (they don't need to be uploaded)
      const googleDriveFiles = filesToProcess.filter(
        attachment => attachment.source === 'google_drive'
      );


      // Upload files to storage
      const updatedAttachments = await uploadFilesToStorage(
        filesToUpload,
        userId,
        positId
      );

      // Combine uploaded files with Google Drive files
      const allAttachments = [...updatedAttachments, ...googleDriveFiles];

      // Revoke object URLs for files that now have permanent URLs
      updatedAttachments.forEach(attachment => {
        const originalAttachment = attachedFiles.find(file => file.id === attachment.id);
        if (originalAttachment && originalAttachment.url !== attachment.url) {
          revokeObjectURL(originalAttachment.url);
        }
      });

      // Store file references in the database
      await storeFileReferences(allAttachments, userId, positId);

      
      // Return all attachments
      return allAttachments;
    } catch (error) {
      handleError(error, 'uploadFiles', {
        category: ErrorCategory.FILE_UPLOAD,
        toastTitle: "Upload Error"
      });
      return attachedFiles;
    } finally {
      // Release locks
      uploadInProgressRef.current = false;
      setIsUploading(false);
      // Use a small delay before releasing the UI lock to prevent rapid consecutive uploads
      setTimeout(() => {
        setIsUploadLocked(false);
      }, 500);
    }
  };

  return {
    attachedFiles,
    fileMap: fileMap.current,
    selectedFile,
    isUploading,
    addFile,
    addGoogleDriveFile,
    removeFile,
    selectFile,
    clearFiles,
    uploadFiles,
    isUploadLocked
  };
} 